import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { toast } from 'sonner';
import { SortableTableHead, sortData } from '../ui/sortable-table';
import {
  RefreshCw,
  UserPlus,
  Users,
  Shield,
  ShieldCheck,
  ShieldAlert,
  MoreVertical,
  Clock,
  CheckCircle2,
  XCircle,
  Edit,
  Trash2,
  Key,
  Crown,
  Headphones,
  CreditCard,
  Code,
  Eye,
  X,
  Check,
  Building2,
  FileText,
  Settings,
  BarChart3,
  MessageSquare,
  Zap,
  AlertTriangle,
  Ban,
  UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detailed role permissions
const ROLE_PERMISSIONS = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'amber',
    description: 'Full access to all features and settings',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: true },
          { name: 'View Revenue Reports', granted: true },
          { name: 'View System Health', granted: true },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: true },
          { name: 'Delete Customers', granted: true },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: true },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: true },
          { name: 'Search Users', granted: true },
          { name: 'Impersonate Users', granted: true },
          { name: 'Reset User Passwords', granted: true },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: true },
          { name: 'Change Plans', granted: true },
          { name: 'Issue Refunds', granted: true },
          { name: 'Apply Credits', granted: true },
          { name: 'Cancel Subscriptions', granted: true },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: true },
          { name: 'Create Tickets', granted: true },
          { name: 'Assign Tickets', granted: true },
          { name: 'Close Tickets', granted: true },
          { name: 'Delete Tickets', granted: true },
          { name: 'Manage Templates', granted: true },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: true },
          { name: 'Invite Staff', granted: true },
          { name: 'Edit Staff Roles', granted: true },
          { name: 'Remove Staff', granted: true },
          { name: 'Manage Permissions', granted: true },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: true },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: true },
          { name: 'Edit Settings', granted: true },
        ]
      },
    }
  },
  super_admin: {
    label: 'Super Admin',
    icon: ShieldCheck,
    color: 'violet',
    description: 'Almost full access, can manage most operations',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: true },
          { name: 'View Revenue Reports', granted: true },
          { name: 'View System Health', granted: true },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: true },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: true },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: true },
          { name: 'Search Users', granted: true },
          { name: 'Impersonate Users', granted: true },
          { name: 'Reset User Passwords', granted: true },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: true },
          { name: 'Change Plans', granted: true },
          { name: 'Issue Refunds', granted: true },
          { name: 'Apply Credits', granted: true },
          { name: 'Cancel Subscriptions', granted: true },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: true },
          { name: 'Create Tickets', granted: true },
          { name: 'Assign Tickets', granted: true },
          { name: 'Close Tickets', granted: true },
          { name: 'Delete Tickets', granted: true },
          { name: 'Manage Templates', granted: true },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: true },
          { name: 'Invite Staff', granted: true },
          { name: 'Edit Staff Roles', granted: true },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: true },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: true },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
  support_manager: {
    label: 'Support Manager',
    icon: Headphones,
    color: 'blue',
    description: 'Manage support team and handle escalations',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: false },
          { name: 'View Revenue Reports', granted: false },
          { name: 'View System Health', granted: true },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: false },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: true },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: true },
          { name: 'Search Users', granted: true },
          { name: 'Impersonate Users', granted: false },
          { name: 'Reset User Passwords', granted: true },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: true },
          { name: 'Change Plans', granted: false },
          { name: 'Issue Refunds', granted: false },
          { name: 'Apply Credits', granted: false },
          { name: 'Cancel Subscriptions', granted: false },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: true },
          { name: 'Create Tickets', granted: true },
          { name: 'Assign Tickets', granted: true },
          { name: 'Close Tickets', granted: true },
          { name: 'Delete Tickets', granted: false },
          { name: 'Manage Templates', granted: true },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: true },
          { name: 'Invite Staff', granted: false },
          { name: 'Edit Staff Roles', granted: false },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: false },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: false },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
  support_agent: {
    label: 'Support Agent',
    icon: Users,
    color: 'cyan',
    description: 'Handle customer support tickets',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: false },
          { name: 'View Revenue Reports', granted: false },
          { name: 'View System Health', granted: false },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: false },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: true },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: true },
          { name: 'Search Users', granted: true },
          { name: 'Impersonate Users', granted: false },
          { name: 'Reset User Passwords', granted: false },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: false },
          { name: 'Change Plans', granted: false },
          { name: 'Issue Refunds', granted: false },
          { name: 'Apply Credits', granted: false },
          { name: 'Cancel Subscriptions', granted: false },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: false },
          { name: 'View Assigned Tickets', granted: true },
          { name: 'Create Tickets', granted: true },
          { name: 'Assign Tickets', granted: false },
          { name: 'Close Tickets', granted: true },
          { name: 'Delete Tickets', granted: false },
          { name: 'Manage Templates', granted: false },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: false },
          { name: 'Invite Staff', granted: false },
          { name: 'Edit Staff Roles', granted: false },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: false },
          { name: 'View Audit Log', granted: false },
          { name: 'Manage Feature Flags', granted: false },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
  billing_specialist: {
    label: 'Billing Specialist',
    icon: CreditCard,
    color: 'emerald',
    description: 'Manage billing, refunds, and subscriptions',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: true },
          { name: 'View Revenue Reports', granted: true },
          { name: 'View System Health', granted: false },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: false },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: false },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: false },
          { name: 'Search Users', granted: false },
          { name: 'Impersonate Users', granted: false },
          { name: 'Reset User Passwords', granted: false },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: true },
          { name: 'Change Plans', granted: true },
          { name: 'Issue Refunds', granted: true },
          { name: 'Apply Credits', granted: true },
          { name: 'Cancel Subscriptions', granted: true },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: false },
          { name: 'View Billing Tickets', granted: true },
          { name: 'Create Tickets', granted: false },
          { name: 'Assign Tickets', granted: false },
          { name: 'Close Tickets', granted: false },
          { name: 'Delete Tickets', granted: false },
          { name: 'Manage Templates', granted: false },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: false },
          { name: 'Invite Staff', granted: false },
          { name: 'Edit Staff Roles', granted: false },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: false },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: false },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
  developer: {
    label: 'Developer',
    icon: Code,
    color: 'orange',
    description: 'Access to system logs and feature flags',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: false },
          { name: 'View Revenue Reports', granted: false },
          { name: 'View System Health', granted: true },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: false },
          { name: 'Edit Customers', granted: false },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: false },
          { name: 'Add Internal Notes', granted: false },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: false },
          { name: 'Search Users', granted: false },
          { name: 'Impersonate Users', granted: false },
          { name: 'Reset User Passwords', granted: false },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: false },
          { name: 'Change Plans', granted: false },
          { name: 'Issue Refunds', granted: false },
          { name: 'Apply Credits', granted: false },
          { name: 'Cancel Subscriptions', granted: false },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: false },
          { name: 'View Bug Reports', granted: true },
          { name: 'Create Tickets', granted: false },
          { name: 'Assign Tickets', granted: false },
          { name: 'Close Tickets', granted: false },
          { name: 'Delete Tickets', granted: false },
          { name: 'Manage Templates', granted: false },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: false },
          { name: 'Invite Staff', granted: false },
          { name: 'Edit Staff Roles', granted: false },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: true },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: true },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
  auditor: {
    label: 'Auditor',
    icon: Eye,
    color: 'slate',
    description: 'Read-only access for compliance auditing',
    categories: {
      'Dashboard & Analytics': {
        permissions: [
          { name: 'View Dashboard', granted: true },
          { name: 'View Analytics', granted: true },
          { name: 'View Revenue Reports', granted: true },
          { name: 'View System Health', granted: true },
        ]
      },
      'Customer Management': {
        permissions: [
          { name: 'View Customers', granted: true },
          { name: 'Edit Customers', granted: false },
          { name: 'Delete Customers', granted: false },
          { name: 'View Customer Details', granted: true },
          { name: 'Add Internal Notes', granted: false },
        ]
      },
      'User Management': {
        permissions: [
          { name: 'View Users', granted: true },
          { name: 'Search Users', granted: true },
          { name: 'Impersonate Users', granted: false },
          { name: 'Reset User Passwords', granted: false },
        ]
      },
      'Billing & Subscriptions': {
        permissions: [
          { name: 'View Billing', granted: true },
          { name: 'Change Plans', granted: false },
          { name: 'Issue Refunds', granted: false },
          { name: 'Apply Credits', granted: false },
          { name: 'Cancel Subscriptions', granted: false },
        ]
      },
      'Support Tickets': {
        permissions: [
          { name: 'View All Tickets', granted: true },
          { name: 'Create Tickets', granted: false },
          { name: 'Assign Tickets', granted: false },
          { name: 'Close Tickets', granted: false },
          { name: 'Delete Tickets', granted: false },
          { name: 'Manage Templates', granted: false },
        ]
      },
      'Staff Management': {
        permissions: [
          { name: 'View Staff', granted: true },
          { name: 'Invite Staff', granted: false },
          { name: 'Edit Staff Roles', granted: false },
          { name: 'Remove Staff', granted: false },
          { name: 'Manage Permissions', granted: false },
        ]
      },
      'System Settings': {
        permissions: [
          { name: 'View System Logs', granted: true },
          { name: 'View Audit Log', granted: true },
          { name: 'Manage Feature Flags', granted: false },
          { name: 'Edit Settings', granted: false },
        ]
      },
    }
  },
};

// Status configurations
const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400' },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
  suspended: { label: 'Suspended', color: 'bg-red-500/20 text-red-400' },
  inactive: { label: 'Inactive', color: 'bg-muted0/20 text-muted-foreground' }
};

// Category icons
const CATEGORY_ICONS = {
  'Dashboard & Analytics': BarChart3,
  'Customer Management': Building2,
  'User Management': Users,
  'Billing & Subscriptions': CreditCard,
  'Support Tickets': MessageSquare,
  'Staff Management': Shield,
  'System Settings': Settings,
};

export default function OwnerAdminStaff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, member: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, member: null });
  const [permissionsDialog, setPermissionsDialog] = useState({ open: false, role: null });
  const [credsDialog, setCredsDialog] = useState({ open: false, email: '', tempPassword: '', emailSent: false });
  const [sort, setSort] = useState({ key: 'name', direction: 'asc' });
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [search, setSearch] = useState('');
  
  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('support_agent');
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/staff`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStaff(data.staff || []);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Filter and sort staff
  const filteredAndSortedStaff = useMemo(() => {
    let filtered = staff;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = staff.filter(s => 
        s.name?.toLowerCase().includes(searchLower) ||
        s.email?.toLowerCase().includes(searchLower) ||
        ROLE_PERMISSIONS[s.role]?.label?.toLowerCase().includes(searchLower)
      );
    }
    return sortData(filtered, sort);
  }, [staff, search, sort]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteName || !inviteRole) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/staff/invite`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          name: inviteName,
          role: inviteRole
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.email_sent ? 'Invite email sent' : 'Staff created — email failed, copy the password below');
        setCredsDialog({ open: true, email: inviteEmail, tempPassword: data.temp_password, emailSent: !!data.email_sent });
        setInviteDialog(false);
        setInviteEmail('');
        setInviteName('');
        setInviteRole('support_agent');
        fetchStaff();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to invite staff');
      }
    } catch (error) {
      toast.error('Failed to invite staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editDialog.member) return;
    
    setSubmitting(true);
    try {
      const updates = {};
      if (editName && editName !== editDialog.member.name) updates.name = editName;
      if (editRole && editRole !== editDialog.member.role) updates.role = editRole;
      if (editStatus && editStatus !== editDialog.member.status) updates.status = editStatus;
      
      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setEditDialog({ open: false, member: null });
        return;
      }
      
      const response = await fetch(`${API_URL}/api/owner-admin/staff/${editDialog.member.admin_id}`, {
        credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        toast.success('Staff member updated');
        setEditDialog({ open: false, member: null });
        fetchStaff();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update staff');
      }
    } catch (error) {
      toast.error('Failed to update staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.member) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/staff/${deleteDialog.member.admin_id}`, {
        credentials: 'include',
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Staff member removed');
        setDeleteDialog({ open: false, member: null });
        fetchStaff();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove staff');
      }
    } catch (error) {
      toast.error('Failed to remove staff');
    } finally {
      setSubmitting(false);
    }
  };

  // Bulk actions
  const handleBulkSuspend = async () => {
    if (selectedStaff.length === 0) return;
    
    setSubmitting(true);
    try {
      const promises = selectedStaff.map(adminId => 
        fetch(`${API_URL}/api/owner-admin/staff/${adminId}`, {
          credentials: 'include',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'suspended' })
        })
      );
      
      await Promise.all(promises);
      toast.success(`Suspended ${selectedStaff.length} staff member(s)`);
      setSelectedStaff([]);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to suspend staff members');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedStaff.length === 0) return;
    
    setSubmitting(true);
    try {
      const promises = selectedStaff.map(adminId => 
        fetch(`${API_URL}/api/owner-admin/staff/${adminId}`, {
          credentials: 'include',
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'active' })
        })
      );
      
      await Promise.all(promises);
      toast.success(`Activated ${selectedStaff.length} staff member(s)`);
      setSelectedStaff([]);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to activate staff members');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (member) => {
    setEditName(member.name);
    setEditRole(member.role);
    setEditStatus(member.status);
    setEditDialog({ open: true, member });
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const config = ROLE_PERMISSIONS[role];
    if (!config) return <Badge className="bg-muted0/20 text-muted-foreground">{role}</Badge>;
    const Icon = config.icon;
    return (
      <Badge className={`bg-${config.color}-500/20 text-${config.color}-400 cursor-pointer hover:opacity-80`}
        onClick={() => setPermissionsDialog({ open: true, role })}
      >
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStaff(filteredAndSortedStaff.filter(s => s.role !== 'owner').map(s => s.admin_id));
    } else {
      setSelectedStaff([]);
    }
  };

  const handleSelectStaff = (adminId, checked) => {
    if (checked) {
      setSelectedStaff(prev => [...prev, adminId]);
    } else {
      setSelectedStaff(prev => prev.filter(id => id !== adminId));
    }
  };

  const isAllSelected = filteredAndSortedStaff.filter(s => s.role !== 'owner').length > 0 && 
    selectedStaff.length === filteredAndSortedStaff.filter(s => s.role !== 'owner').length;

  // Permission count for role
  const getPermissionCounts = (role) => {
    const roleData = ROLE_PERMISSIONS[role];
    if (!roleData) return { granted: 0, total: 0 };
    
    let granted = 0;
    let total = 0;
    
    Object.values(roleData.categories).forEach(cat => {
      cat.permissions.forEach(p => {
        total++;
        if (p.granted) granted++;
      });
    });
    
    return { granted, total };
  };

  if (loading && !staff.length) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48 bg-white/10" />
          <Skeleton className="h-10 w-32 bg-white/10" />
        </div>
        <Card className="bg-card border-white/15">
          <CardContent className="p-6">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full mb-4 bg-white/10" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Staff Management</h1>
          <p className="text-muted-foreground">Manage admin team members and permissions</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={fetchStaff} 
            className="border-white/20 text-muted-foreground/80"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setInviteDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Staff
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedStaff.length > 0 && (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-emerald-500/20 text-emerald-400">
                  {selectedStaff.length} selected
                </Badge>
                <span className="text-muted-foreground text-sm">
                  staff member(s) selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkActivate}
                  disabled={submitting}
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Activate
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkSuspend}
                  disabled={submitting}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Suspend
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedStaff([])}
                  className="text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Staff</p>
                <p className="text-2xl font-bold text-white">{staff.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {staff.filter(s => s.status === 'active').length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {staff.filter(s => s.status === 'pending').length}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">MFA Enabled</p>
                <p className="text-2xl font-bold text-violet-400">
                  {staff.filter(s => s.mfa_enabled).length}
                </p>
              </div>
              <Shield className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6">
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white"
              data-testid="staff-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Staff Table */}
      <Card className="bg-card border-white/15">
        <CardContent className="p-0">
          {filteredAndSortedStaff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No staff members found</p>
              <Button 
                variant="outline" 
                className="mt-4 border-white/20"
                onClick={() => setInviteDialog(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite First Staff Member
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/15 hover:bg-white/[0.07]">
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      className="border-white/20"
                    />
                  </TableHead>
                  <SortableTableHead sortKey="name" currentSort={sort} onSort={setSort}>
                    Name
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="role" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={Object.entries(ROLE_PERMISSIONS).map(([k, v]) => ({ value: k, label: v.label }))}
                  >
                    Role
                  </SortableTableHead>
                  <SortableTableHead 
                    sortKey="status" 
                    currentSort={sort} 
                    onSort={setSort}
                    filterOptions={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead sortKey="mfa_enabled" currentSort={sort} onSort={setSort}>
                    MFA
                  </SortableTableHead>
                  <SortableTableHead sortKey="last_login" currentSort={sort} onSort={setSort}>
                    Last Login
                  </SortableTableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedStaff.map((member) => (
                  <TableRow 
                    key={member.admin_id} 
                    className={`border-white/15 hover:bg-white/[0.07] ${
                      selectedStaff.includes(member.admin_id) ? 'bg-emerald-500/5' : ''
                    }`}
                    data-testid={`staff-row-${member.admin_id}`}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedStaff.includes(member.admin_id)}
                        onCheckedChange={(checked) => handleSelectStaff(member.admin_id, checked)}
                        disabled={member.role === 'owner'}
                        className="border-white/20"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                          <span className="text-white font-medium">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(member.role)}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-white"
                          onClick={() => setPermissionsDialog({ open: true, role: member.role })}
                          title="View permissions"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_CONFIG[member.status]?.color || STATUS_CONFIG.inactive.color}>
                        {STATUS_CONFIG[member.status]?.label || member.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.mfa_enabled ? (
                        <ShieldCheck className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <ShieldAlert className="h-5 w-5 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(member.last_login)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-muted-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setPermissionsDialog({ open: true, role: member.role })}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Permissions
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(member)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Key className="h-4 w-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-400"
                            onClick={() => setDeleteDialog({ open: true, member })}
                            disabled={member.role === 'owner'}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions Reference - Enhanced */}
      <Card className="bg-card border-white/15">
        <CardHeader>
          <CardTitle className="text-white">Role Permissions Overview</CardTitle>
          <CardDescription className="text-muted-foreground">
            Click on any role to view detailed permissions. Each role has specific access levels.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Object.entries(ROLE_PERMISSIONS).map(([key, config]) => {
              const Icon = config.icon;
              const counts = getPermissionCounts(key);
              return (
                <div 
                  key={key} 
                  className={`p-4 rounded-lg border border-white/15 bg-card/50 cursor-pointer hover:border-${config.color}-500/50 transition-colors`}
                  onClick={() => setPermissionsDialog({ open: true, role: key })}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg bg-${config.color}-500/10`}>
                      <Icon className={`h-5 w-5 text-${config.color}-400`} />
                    </div>
                    <div>
                      <span className="font-medium text-white">{config.label}</span>
                      <p className="text-xs text-muted-foreground">
                        {counts.granted}/{counts.total} permissions
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                  <div className="mt-3 flex items-center gap-1">
                    <div 
                      className={`h-1.5 rounded-full bg-${config.color}-500`}
                      style={{ width: `${(counts.granted / counts.total) * 100}%` }}
                    />
                    <div 
                      className="h-1.5 rounded-full bg-white/10 flex-1"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Detail Dialog */}
      <Dialog open={permissionsDialog.open} onOpenChange={(open) => !open && setPermissionsDialog({ open: false, role: null })}>
        <DialogContent className="bg-card border-white/15 max-w-2xl max-h-[80vh] overflow-y-auto">
          {permissionsDialog.role && ROLE_PERMISSIONS[permissionsDialog.role] && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = ROLE_PERMISSIONS[permissionsDialog.role].icon;
                    const color = ROLE_PERMISSIONS[permissionsDialog.role].color;
                    return (
                      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
                        <Icon className={`h-6 w-6 text-${color}-400`} />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle className="text-white text-xl">
                      {ROLE_PERMISSIONS[permissionsDialog.role].label} Permissions
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      {ROLE_PERMISSIONS[permissionsDialog.role].description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="py-4">
                <Accordion type="multiple" defaultValue={Object.keys(ROLE_PERMISSIONS[permissionsDialog.role].categories)} className="space-y-2">
                  {Object.entries(ROLE_PERMISSIONS[permissionsDialog.role].categories).map(([category, data]) => {
                    const CategoryIcon = CATEGORY_ICONS[category] || Settings;
                    const grantedCount = data.permissions.filter(p => p.granted).length;
                    const totalCount = data.permissions.length;
                    
                    return (
                      <AccordionItem key={category} value={category} className="border border-white/15 rounded-lg overflow-hidden">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/[0.07]">
                          <div className="flex items-center gap-3">
                            <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-white font-medium">{category}</span>
                            <Badge variant="outline" className={`ml-2 ${grantedCount === totalCount ? 'border-emerald-500/50 text-emerald-400' : 'border-white/20 text-muted-foreground'}`}>
                              {grantedCount}/{totalCount}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-2 mt-2">
                            {data.permissions.map((permission, idx) => (
                              <div 
                                key={idx}
                                className={`flex items-center justify-between p-2 rounded ${permission.granted ? 'bg-emerald-500/5' : 'bg-white/[0.07]'}`}
                              >
                                <span className={permission.granted ? 'text-white' : 'text-muted-foreground'}>
                                  {permission.name}
                                </span>
                                {permission.granted ? (
                                  <Check className="h-4 w-4 text-emerald-400" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Invite Staff Member</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Send an invitation to join the admin team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Email *</label>
              <Input
                type="email"
                placeholder="staff@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Name *</label>
              <Input
                placeholder="John Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Role *</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_PERMISSIONS).filter(([k]) => k !== 'owner').map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="h-4 w-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {inviteRole && ROLE_PERMISSIONS[inviteRole] && (
                <p className="text-xs text-muted-foreground mt-2">
                  {ROLE_PERMISSIONS[inviteRole].description}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={submitting || !inviteEmail || !inviteName}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Credentials Dialog */}
      <Dialog open={credsDialog.open} onOpenChange={(open) => !open && setCredsDialog({ open: false, email: '', tempPassword: '', emailSent: false })}>
        <DialogContent className="owner-admin-scope bg-card border-white/15 max-w-md" data-testid="staff-creds-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Staff member added</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {credsDialog.emailSent
                ? `An invite email with these credentials was sent to ${credsDialog.email}.`
                : `Email delivery failed — share these credentials with ${credsDialog.email} manually.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-lg bg-white/[0.07] border border-white/15">
              <p className="text-xs text-muted-foreground mb-1">Login email</p>
              <p className="text-sm text-white font-mono" data-testid="staff-creds-email">{credsDialog.email}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/[0.07] border border-white/15">
              <p className="text-xs text-muted-foreground mb-1">Temporary password</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-white font-mono" data-testid="staff-creds-password">{credsDialog.tempPassword}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/20 h-7 text-xs"
                  data-testid="staff-creds-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(credsDialog.tempPassword);
                    toast.success('Password copied');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">They should change this password on first login (Settings → Security). Sign-in page: /owner-admin/login</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredsDialog({ open: false, email: '', tempPassword: '', emailSent: false })} className="bg-emerald-600 hover:bg-emerald-700">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, member: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Staff Member</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update {editDialog.member?.name}&apos;s information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Name</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Role</label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_PERMISSIONS).map(([key, config]) => (
                    <SelectItem key={key} value={key} disabled={key === 'owner'}>
                      <div className="flex items-center gap-2">
                        <config.icon className="h-4 w-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Status</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, member: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, member: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Remove Staff Member</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to remove <strong className="text-white">{deleteDialog.member?.name}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, member: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Removing...' : 'Remove Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
